import { useQuery } from "@tanstack/react-query";
import { getVimeoVideoLink } from "@/api/api";
import type { VimeoResponse } from "@/api/vimeo.types";
import { getVimeoId } from "@/pages/Workshop/MultimediaCard.utils";

/**
 * Ekstraktuje fragment Vimeo z HTML i dzieli go na części przed i po
 * Wykrywa pierwszy div z iframe zawierający player.vimeo.com
 * Wycina również script tag z player.vimeo.com/api/player.js jeśli występuje
 */
function extractVimeoFragment(html: string): {
	vimeoId: number | null;
	htmlBeforeVimeo: string;
	htmlAfterVimeo: string;
	hasFragment: boolean;
} {
	if (!html || typeof html !== "string") {
		return { vimeoId: null, htmlBeforeVimeo: html, htmlAfterVimeo: "", hasFragment: false };
	}

	// Wzorzec dla div z iframe Vimeo - może mieć różne style padding lub być w prostym iframe
	// Szukamy zarówno div z padding jak i bezpośrednio iframe
	const vimeoDivPattern = /<div[^>]*style="[^"]*padding[^"]*"[^>]*>[\s\S]*?<iframe[^>]*src=["']([^"']*player\.vimeo\.com[^"']*)["'][^>]*>[\s\S]*?<\/iframe>[\s\S]*?<\/div>/i;
	const vimeoIframePattern = /<iframe[^>]*src=["']([^"']*player\.vimeo\.com[^"']*)["'][^>]*>[\s\S]*?<\/iframe>/i;
	
	let vimeoMatch = html.match(vimeoDivPattern);
	let iframeSource: string | undefined;
	
	if (!vimeoMatch) {
		// Jeśli nie znaleziono div z padding, spróbuj znaleźć bezpośrednio iframe
		const iframeMatch = html.match(vimeoIframePattern);
		if (iframeMatch) {
			iframeSource = iframeMatch[1];
			// Użyj całego iframe jako match
			vimeoMatch = iframeMatch;
		}
	} else {
		iframeSource = vimeoMatch[1];
	}
	
	if (!vimeoMatch || !iframeSource) {
		return { vimeoId: null, htmlBeforeVimeo: html, htmlAfterVimeo: "", hasFragment: false };
	}

	// Wyciągnij Vimeo ID z iframe src
	const vimeoIdString = getVimeoId(iframeSource);
	
	if (!vimeoIdString) {
		return { vimeoId: null, htmlBeforeVimeo: html, htmlAfterVimeo: "", hasFragment: false };
	}

	const vimeoId = parseInt(vimeoIdString, 10);
	if (isNaN(vimeoId)) {
		return { vimeoId: null, htmlBeforeVimeo: html, htmlAfterVimeo: "", hasFragment: false };
	}

	// Znajdź indeks początku i końca fragmentu
	const vimeoMatchIndex = vimeoMatch.index ?? -1;
	if (vimeoMatchIndex === -1) {
		return { vimeoId: null, htmlBeforeVimeo: html, htmlAfterVimeo: "", hasFragment: false };
	}

	const fragmentStartIndex = vimeoMatchIndex;
	const fragmentEndIndex = vimeoMatchIndex + vimeoMatch[0].length;

	// Sprawdź czy po iframe jest script tag z player.vimeo.com/api/player.js
	const htmlAfterIframe = html.substring(fragmentEndIndex);
	const scriptPattern = /<p[^>]*>[\s\S]*?<script[^>]*src=["'][^"']*player\.vimeo\.com\/api\/player\.js[^"']*["'][^>]*>[\s\S]*?<\/script>[\s\S]*?<\/p>/i;
	const scriptMatch = htmlAfterIframe.match(scriptPattern);
	
	let actualEndIndex = fragmentEndIndex;
	if (scriptMatch && scriptMatch.index !== undefined) {
		// Jeśli znaleziono script tag, usuń go również
		actualEndIndex = fragmentEndIndex + scriptMatch.index + scriptMatch[0].length;
	}

	// Podziel HTML na części przed i po Vimeo
	const htmlBeforeVimeo = html.substring(0, fragmentStartIndex);
	const htmlAfterVimeo = html.substring(actualEndIndex);

	return {
		vimeoId,
		htmlBeforeVimeo: htmlBeforeVimeo.trim(),
		htmlAfterVimeo: htmlAfterVimeo.trim(),
		hasFragment: true,
	};
}

/**
 * Hook do przetwarzania HTML i ekstrakcji danych Vimeo
 * Wykrywa embedded Vimeo w dowolnym HTML i dzieli go na części przed i po
 */
export function useVimeoHtmlProcessor(html: string | undefined): {
	vimeoId: number | null;
	vimeoData: VimeoResponse | undefined;
	htmlBeforeVimeo: string;
	htmlAfterVimeo: string;
	hasFragment: boolean;
	isLoading: boolean;
} {
	const { vimeoId, htmlBeforeVimeo, htmlAfterVimeo, hasFragment } = extractVimeoFragment(html || "");

	const { data: vimeoData, isLoading } = useQuery<VimeoResponse | undefined>({
		queryKey: ["vimeoVideoLink", vimeoId],
		queryFn: () => (vimeoId ? getVimeoVideoLink(vimeoId.toString()) : undefined),
		enabled: hasFragment && !!vimeoId,
	});

	return {
		vimeoId,
		vimeoData,
		htmlBeforeVimeo,
		htmlAfterVimeo,
		hasFragment,
		isLoading,
	};
}

